/**
 * Host-owned state of a module's INLINE actions (PPL-417): the server's
 * answer to M3's keep / skip and M11's rsvp.
 *
 * A module that renders an inline control needs to show what happened to
 * it — "kept · undo", "going" — and only the host that made the call knows.
 * The state travels by context rather than props so a module nested inside
 * another (M16's `expandsInto`) gets it too, and so a module hosted without
 * one (the fixtures page) simply keeps its own optimistic state.
 */

import { createContext, useContext } from 'react';

export type ReviewInlineStatus =
  /** Accepted — "kept"; `undoable` once the call has landed (undo is terminal). */
  | 'kept'
  /** Rejected, deferred — "skipped"; `undoable` while the server's undo window is open. */
  | 'skipped'
  /** A kept item undone — "not saved". */
  | 'undone'
  /** The undo was refused because the user had edited the value since — it stays kept. */
  | 'conflicted'
  /** The accept did not land — the buttons come back with a status line. */
  | 'failed';

export interface ReviewInline {
  status: ReviewInlineStatus;
  undoable: boolean;
}

export type RsvpInlineStatus = 'pending' | 'going' | 'failed';

export interface HomeInlineState {
  review: (itemId: string) => ReviewInline | null;
  rsvp: (eventId: string) => RsvpInlineStatus | null;
}

/** `null` = no host; the module falls back to its own optimistic state. */
export const HomeInlineContext = createContext<HomeInlineState | null>(null);

export function useHomeInline(): HomeInlineState | null {
  return useContext(HomeInlineContext);
}
